import LoadObject from "./LoadObject";
import invariant from "invariant";
import dotProp from "dot-prop-immutable";

/**
 * I allow translating a value at some path in a state object through a mapping
 * object or function. If the path doesn't exist, the same state will be
 * returned. If a mapping object is used, and it lacks a key for the current
 * value, no mapping will be performed. If a mapping function is used, it will
 * be invoked with the current value to provide the new value. No null checks
 * are performed on the current value, only the path to the value. That is, you
 * _can_ map null into something else.
 *
 * @param state The state to update (or return unmodified)
 * @param propertyChain An array of string property names to traverse to get to
 *  the value to translate (if any)
 * @param valueMap Either an Object containing old-to-new mappings, or a
 *  function accepting an old value and returning the corresponding new one.
 * @return {*} the updated state
 */
export const translateValue = (state, propertyChain, valueMap) => {
    if (valueMap == null) {
        return state;
    }
    let curr = state;
    for (const link of propertyChain) {
        if (curr == null || !curr.hasOwnProperty(link)) {
            // don't have a current one
            return state;
        }
        curr = curr[link];
    }
    let next;
    if (typeof valueMap === "function") {
        next = valueMap(curr);
    } else {
        if (!valueMap.hasOwnProperty(curr)) {
            // there's no translation for it
            return state;
        }
        next = valueMap[curr];
    }
    if (next === curr) {
        // the new one is the same.
        return state;
    }
    return dotProp.set(state, propertyChain, next);
};

/**
 * I provide hot-loading logic that a store may plug into any selector which
 * returns a LoadObject that should auto-populate. It takes two callbacks, one
 * to obtain the store's LO in question, and one to initiate loading into the
 * LO (almost certainly via dispatching a "start loading" action). It's expected
 * that this is mostly useful for 'loading' operations, but can be used for any
 * LO action.
 *
 * If getLO returns falsey, an empty LO will be automatically substituted.
 *
 * If shouldLoad returns falsey, the LO will be returned as-is.
 *
 * If shouldLoad returns truthy, doLoad will be invoked, and a new LO will be
 * returned in a pending state with an unresolved Promise bound to the eventual
 * resolution of what doLoad initiated. Note that _which_ pending state the LO
 * will be in is undefined. On the next tick, the LO will be in the pending
 * state that doLoad resulted in. This is a kludge to facilitate a synchronously
 * returned Promise for non-reactive flows, and this functionality is done at
 * the expense of the LO's internal state that single tick.
 *
 * @param getLO return the current LO from the store
 * @param doLoad initiate loading, which must synchronously update the store so
 *  that the next invocation of getLO returns a non-empty (loading) LO.
 * @param shouldLoad optional function accepting a LO to indicate whether it
 *  needs to be loaded. LoadObject.isEmpty() will be used by default.
 * @return {LoadObject}
 */

/* hotLoadObjectActiveCount is used purely for AppDrivers wait action.
 * because HLO starts the promise in the next frame to avoid reentrant
 * flux dispatches we need to help the wait action know that the currant frame
 * should also be considered "loading".
 */
window.hotLoadObjectActiveCount = 0;

export const hotLoadObject = (
    getLO,
    doLoad,
    shouldLoad,
) => {
    const defaultShouldLoad = shouldLoad == null;
    if (defaultShouldLoad) {
        shouldLoad = lo => lo.isEmpty();
    }
    let lo = getLO() || LoadObject.empty();
    if (shouldLoad(lo)) {
        window.hotLoadObjectActiveCount++;
        /*
        Note that this is more complex that it seems like it should be so that
        we can get a synchronously-created Promise for the calling code can
        react to the eventual load. This is only needed for the non-reactive
        flows; the Promises will eventually be completely removed.

        A thunk is queued for the next tick to avoid reentrant dispatches (the
        expected behaviour of doLoad), but doing so means the Store doesn't get
        it's pending LO for the remainder of this tick. That, in turn, means
        that multiple requests for the same LO in the same tick will all queue
        separate (but equivalent) thunks. The second `shouldLoad` check inside
        the thunk will make all subsequent thunks "do nothing".
        */
        lo = lo.loading(new Promise(resolve => {
            // eslint-disable-next-line no-settimeout
            setTimeout(() => {
                let nextLO = getLO();
                if (nextLO == null || shouldLoad(nextLO)) {
                    doLoad();
                    nextLO = getLO();
                    invariant(
                        nextLO != null,
                        "doLoad must warm up getLO's cache",
                    );
                    if (defaultShouldLoad) {
                        invariant(!shouldLoad(lo), "doLoad must create a pending LO");
                    }
                }
                resolve(nextLO.promise()
                    .finally(() => {
                        window.hotLoadObjectActiveCount--;
                    }));
            }, 0);
        }));
    }
    return lo;
};



// WEBPACK FOOTER //
// ./app/data/storeUtils.js